import { AuiIf, groupPartByType, MessagePrimitive } from '@assistant-ui/react'
// import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown'
import '@assistant-ui/react-markdown/styles/dot.css'
// import remarkGfm from 'remark-gfm'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import {
  Reasoning,
  ReasoningContent,
  ReasoningRoot,
  ReasoningText,
  ReasoningTrigger,
} from '@/components/assistant-ui/reasoning'
import { useSessionStorage } from '@/hooks'
import { getInitials } from '@/lib/utils'
import { IconBg } from '@/components/ui/IconBg'
import { MarkdownText } from '@/components/assistant-ui/markdown-text'
import { UserMessageAttachments } from '@/components/assistant-ui/attachment.aui'
import { AssistantActionBar } from './AssistantActionBar'
import { DmpChatToolCall, DmpChatToolGroup } from './DmpChatToolCall'
import { useDmpChatRuntime } from './DmpChatRuntimeProvider'
import type { DmpChatToolDisplayMode } from '../dmpChatToolDisplay'
import { IconV2PersonOutline } from '@icons/IconV2PersonOutline'
import { DmpChatDirectiveText, hasDmpChatDirective } from './DmpChatTriggerPresentation'

export function DmpChatMessage() {
  const [name] = useSessionStorage('name', '')
  const { toolDisplayMode } = useDmpChatRuntime()
  return (
    <MessagePrimitive.Root className="group min-w-0 px-4 py-5">
      <AuiIf condition={({ message }) => message.role === 'user'}>
        <div className="ml-auto flex min-w-0 max-w-3xl items-start justify-end gap-3 sm:gap-5">
          <div className="min-w-0 max-w-[min(100%,48rem)] break-words rounded-xl bg-background px-4 py-4 text-sm text-muted-foreground shadow sm:px-6 sm:py-5">
            <DmpChatMessageText isUser toolDisplayMode={toolDisplayMode} />
            <UserMessageAttachments />
          </div>
          <Avatar className="size-9 shrink-0 sm:size-11">
            <AvatarFallback className="bg-accent text-lg text-accent-foreground">{getInitials(name)}</AvatarFallback>
          </Avatar>
        </div>
      </AuiIf>

      <AuiIf condition={({ message }) => message.role === 'assistant'}>
        <div className="mr-auto flex min-w-0 max-w-full gap-3 sm:gap-5">
          <IconBg className="size-9 shrink-0 translate-y-2 bg-secondary text-primary sm:size-11">
            <IconV2PersonOutline className="size-7" />
          </IconBg>
          <div className="min-w-0 max-w-full flex-1 break-words rounded-lg bg-offwhite px-3 py-3 text-sm text-card-foreground sm:px-4">
            <DmpChatMessageText isUser={false} toolDisplayMode={toolDisplayMode} />
            <AssistantActionBar />
            <AuiIf condition={({ message }) => message.status?.type === 'running'}>
              <span className="mt-2 block text-xs text-muted-foreground" role="status">
                Project Agent is responding…
              </span>
            </AuiIf>
            <AuiIf condition={({ message }) => message.status?.type === 'incomplete'}>
              <span className="mt-2 block text-xs text-destructive" role="status">
                This response did not complete.
              </span>
            </AuiIf>
          </div>
        </div>
      </AuiIf>
    </MessagePrimitive.Root>
  )
}

function DmpChatMessageText({ isUser, toolDisplayMode }: { isUser: boolean; toolDisplayMode: DmpChatToolDisplayMode }) {
  return (
    <MessagePrimitive.GroupedParts
      groupBy={groupPartByType({
        reasoning: ['group-reasoning'],
        'tool-call': ['group-tool'],
      })}
    >
      {({ part, children }) => {
        switch (part.type) {
          case 'group-reasoning': {
            const isStreaming = part.status.type === 'running'
            const isIncomplete = part.status.type === 'incomplete'
            return (
              <ReasoningRoot incomplete={isIncomplete} streaming={isStreaming}>
                <ReasoningTrigger active={isStreaming} incomplete={isIncomplete} />
                <ReasoningContent aria-busy={isStreaming}>
                  <ReasoningText>{children}</ReasoningText>
                </ReasoningContent>
              </ReasoningRoot>
            )
          }
          case 'group-tool':
            return (
              <DmpChatToolGroup count={part.indices.length} status={part.status}>
                {children}
              </DmpChatToolGroup>
            )
          case 'reasoning':
            return part.text.trim() ? <Reasoning {...part} /> : null
          case 'text':
            return isUser && hasDmpChatDirective(part.text) ? <DmpChatDirectiveText {...part} /> : <MarkdownText />
          case 'tool-call':
            return (
              <DmpChatToolCall
                argsText={part.argsText}
                mode={toolDisplayMode}
                result={part.result}
                status={part.status}
                toolName={part.toolName}
              />
            )
          default:
            return null
        }
      }}
    </MessagePrimitive.GroupedParts>
  )
}
